import { useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useMeta } from "@/context/MetaContext";
import { DEPARTMENTS } from "@/constants/workflow";

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** Admin editor for the status colours used across the model, tables and charts. */
export function StatusColorsSection({ settings, onChanged }) {
  const { refresh } = useMeta();
  const [draft, setDraft] = useState(settings.effective_status_colors || {});
  const [saving, setSaving] = useState(false);

  const departments = DEPARTMENTS;
  const values = settings.status_values || {};
  const defaults = settings.default_status_colors || {};

  const setColor = (department, status, hex) =>
    setDraft((prev) => ({
      ...prev,
      [department]: { ...(prev[department] || {}), [status]: hex },
    }));

  const save = async () => {
    const invalid = Object.entries(draft).flatMap(([dept, colors]) =>
      Object.entries(colors || {})
        .filter(([, hex]) => hex && !HEX.test(hex))
        .map(([status]) => `${dept} · ${status}`)
    );
    if (invalid.length) {
      toast.error(`Invalid hex colour: ${invalid[0]}`);
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings", { status_colors: draft });
      await refresh();
      toast.success("Status colours updated");
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { status_colors: {} });
      setDraft(defaults);
      await refresh();
      toast.success("Default palette restored");
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="status-colors-section">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-5">
        <p className="text-[11px] text-slate-500">
          These colours drive the 3D model, status filters, legends, panel details, Overview and
          Panel List. Paste a hex value (e.g. <span className="mono">#A03472</span>) or use the
          picker.
        </p>
        <div className="ml-auto flex gap-2">
          <Button
            data-testid="reset-status-colors"
            variant="outline"
            disabled={saving}
            onClick={resetAll}
            className="rounded-full border-slate-200 text-xs"
          >
            <RotateCcw size={12} className="mr-1.5" /> Restore defaults
          </Button>
          <Button
            data-testid="save-status-colors"
            disabled={saving}
            onClick={save}
            className="rounded-full bg-cyan-500 text-xs text-[#04121a]"
          >
            Save colours
          </Button>
        </div>
      </div>

      {departments.map((dept) => (
        <div
          key={dept.key}
          data-testid={`color-row-${dept.key}`}
          className="rounded-xl border border-slate-200 bg-white p-5"
        >
          <p className="font-display text-sm font-medium">{dept.label}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(values[dept.key] || []).map((status) => {
              const current = draft[dept.key]?.[status] || defaults[dept.key]?.[status] || "#64748B";
              const isDefault = current.toUpperCase() === (defaults[dept.key]?.[status] || "").toUpperCase();
              return (
                <div key={status} className="flex items-center gap-2">
                  <input
                    type="color"
                    data-testid={`color-picker-${dept.key}-${status}`}
                    value={HEX.test(current) ? current : "#64748B"}
                    onChange={(e) => setColor(dept.key, status, e.target.value.toUpperCase())}
                    className="h-8 w-9 shrink-0 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] text-slate-700">{status}</p>
                    <input
                      data-testid={`color-input-${dept.key}-${status}`}
                      value={current}
                      onChange={(e) => setColor(dept.key, status, e.target.value.toUpperCase())}
                      className="mono w-full rounded border border-slate-200 bg-[#EEF2F8] px-2 py-1 text-[10px] uppercase tracking-wider text-slate-600"
                    />
                  </div>
                  {isDefault ? null : (
                    <button
                      data-testid={`color-reset-${dept.key}-${status}`}
                      title="Reset this status"
                      onClick={() => setColor(dept.key, status, defaults[dept.key]?.[status])}
                      className="mono shrink-0 text-[9px] uppercase tracking-wider text-slate-400 hover:text-slate-700"
                    >
                      reset
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
